import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { X, Download } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Button } from './Button';
import { SkeletonLine, SkeletonBlock } from './Skeleton';
import { ErrorState } from './ErrorState';

// Configure marked for GFM
marked.setOptions({ gfm: true, breaks: true });

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'del', 'a', 'img',
    'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'h1', 'h2', 'h3',
    'h4', 'h5', 'h6', 'span', 'div', 'table', 'thead', 'tbody', 'tr',
    'th', 'td', 'hr', 'input', 'sup', 'sub',
  ],
  ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'target', 'rel', 'class', 'type', 'checked', 'disabled'],
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10001;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  padding: 24px;
`;

const Container = styled.div`
  background: ${p => p.theme.colors.backgroundSecondary};
  border-radius: ${p => p.theme.borderRadius.lg};
  border: 1px solid ${p => p.theme.colors.border};
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.lg};
  border-bottom: 1px solid ${p => p.theme.colors.border};
  flex-shrink: 0;
`;

const Title = styled.h3`
  margin: 0;
  font-size: ${p => p.theme.typography.sizes.default};
  font-weight: 600;
  color: ${p => p.theme.colors.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  margin-right: ${p => p.theme.spacing.md};
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: ${p => p.theme.borderRadius.sm};
  background: transparent;
  color: ${p => p.theme.colors.muted};
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${p => p.theme.colors.background};
    color: ${p => p.theme.colors.text};
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${p => p.theme.spacing.lg} ${p => p.theme.spacing.xl};
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${p => p.theme.spacing.sm};
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.lg};
  border-top: 1px solid ${p => p.theme.colors.border};
  flex-shrink: 0;
`;

const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: ${p => p.theme.spacing.md} 0;
`;

const MarkdownBody = styled.div`
  color: ${p => p.theme.colors.text};
  font-size: 15px;
  line-height: 1.7;
  word-wrap: break-word;

  h1, h2, h3, h4, h5, h6 {
    color: ${p => p.theme.colors.primary};
    margin: 1.5em 0 0.5em;
    line-height: 1.3;
    font-weight: 600;
  }

  h1 {
    font-size: 1.8em;
    padding-bottom: 0.3em;
    border-bottom: 2px solid ${p => p.theme.colors.border};
  }

  h2 {
    font-size: 1.45em;
    padding-bottom: 0.25em;
    border-bottom: 1px solid ${p => p.theme.colors.border};
  }

  h3 { font-size: 1.2em; }
  h4 { font-size: 1.05em; }
  h5 { font-size: 0.95em; }
  h6 { font-size: 0.9em; color: ${p => p.theme.colors.muted}; }

  p { margin: 0.8em 0; }

  a {
    color: ${p => p.theme.colors.tint};
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }

  img {
    max-width: 100%;
    border-radius: ${p => p.theme.borderRadius.md};
  }

  ul, ol {
    margin: 0.8em 0;
    padding-left: 1.8em;
  }

  li {
    margin: 0.3em 0;
    &::marker { color: ${p => p.theme.colors.muted}; }
  }

  blockquote {
    margin: 1em 0;
    padding: 0.6em 1em;
    border-left: 3px solid ${p => p.theme.colors.tint};
    background: ${p => p.theme.isDark ? 'rgba(89,202,238,0.06)' : 'rgba(89,202,238,0.04)'};
    border-radius: 0 ${p => p.theme.borderRadius.sm} ${p => p.theme.borderRadius.sm} 0;
    color: ${p => p.theme.colors.muted};
    font-style: italic;

    p { margin: 0.4em 0; }
  }

  code {
    font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
    font-size: 0.88em;
    padding: 0.15em 0.4em;
    border-radius: 4px;
    background: ${p => p.theme.isDark ? 'rgba(89,202,238,0.1)' : 'rgba(35,37,98,0.06)'};
    color: ${p => p.theme.isDark ? '#e8b4f8' : '#7c3aed'};
  }

  pre {
    margin: 1em 0;
    padding: 1em;
    border-radius: ${p => p.theme.borderRadius.md};
    background: ${p => p.theme.isDark ? '#0a0d1f' : '#f1f5f9'};
    border: 1px solid ${p => p.theme.colors.border};
    overflow-x: auto;

    code {
      padding: 0;
      background: none;
      color: ${p => p.theme.isDark ? '#d4d4d8' : '#334155'};
      font-size: 13px;
      line-height: 1.6;
    }
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 1em 0;
    font-size: 14px;
  }

  th, td {
    padding: 8px 12px;
    border: 1px solid ${p => p.theme.colors.border};
    text-align: left;
  }

  th {
    font-weight: 600;
    background: ${p => p.theme.colors.background};
  }

  tr:nth-child(even) td {
    background: ${p => p.theme.isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)'};
  }

  hr {
    border: none;
    border-top: 1px solid ${p => p.theme.colors.border};
    margin: 1.5em 0;
  }

  input[type="checkbox"] {
    margin-right: 6px;
    accent-color: ${p => p.theme.colors.tint};
  }

  del { color: ${p => p.theme.colors.muted}; }
`;

export const MarkdownPreviewModal = ({ isOpen, onClose, fileUrl, fileName }) => {
  const theme = useTheme();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMarkdown = useCallback(async () => {
    if (!fileUrl) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(fileUrl);
      if (!res.ok) throw new Error(`Failed to load file (${res.status})`);
      const text = await res.text();
      setContent(text);
    } catch (err) {
      setError(err.message || 'Failed to load markdown file');
    } finally {
      setLoading(false);
    }
  }, [fileUrl]);

  useEffect(() => {
    if (isOpen && fileUrl) {
      fetchMarkdown();
    }
    return () => { setContent(''); setError(null); };
  }, [isOpen, fileUrl, fetchMarkdown]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const renderedHtml = useMemo(() => {
    if (!content) return '';
    const raw = marked.parse(content);
    return DOMPurify.sanitize(raw, SANITIZE_CONFIG);
  }, [content]);

  const handleDownload = () => {
    if (fileUrl) window.open(fileUrl, '_blank');
  };

  if (!isOpen) return null;

  return (
    <Overlay $isOpen={isOpen} onClick={onClose}>
      <Container theme={theme} onClick={e => e.stopPropagation()}>
        <Header theme={theme}>
          <Title theme={theme}>{fileName || 'Markdown Preview'}</Title>
          <CloseBtn theme={theme} onClick={onClose} title="Close">
            <X size={18} />
          </CloseBtn>
        </Header>

        <Body theme={theme}>
          {loading && (
            <LoadingWrap theme={theme}>
              <SkeletonLine width="50%" height="24px" theme={theme} />
              <SkeletonBlock height="14px" theme={theme} />
              <SkeletonBlock height="14px" theme={theme} />
              <SkeletonLine width="70%" height="14px" theme={theme} />
              <SkeletonBlock height="80px" theme={theme} />
              <SkeletonBlock height="14px" theme={theme} />
              <SkeletonLine width="40%" height="14px" theme={theme} />
            </LoadingWrap>
          )}

          {error && (
            <ErrorState
              theme={theme}
              title="Couldn't load markdown"
              message={error}
              onRetry={fetchMarkdown}
            />
          )}

          {!loading && !error && content && (
            <MarkdownBody
              theme={theme}
              dangerouslySetInnerHTML={{ __html: renderedHtml }}
            />
          )}

          {!loading && !error && !content && isOpen && (
            <p style={{ color: theme.colors.muted, fontStyle: 'italic' }}>
              This file appears to be empty.
            </p>
          )}
        </Body>

        <Footer theme={theme}>
          <Button theme={theme} onClick={onClose}>Close</Button>
          <Button theme={theme} primary onClick={handleDownload}>
            <Download size={16} style={{ marginRight: 6 }} />
            Download
          </Button>
        </Footer>
      </Container>
    </Overlay>
  );
};
